import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

type MatomoProps = {
  siteId: string
  baseUrl: string
}

type MatomoTagManagerProps = {
  containerId: string
  baseUrl: string
}

declare global {
  interface Window {
    _paq?: unknown[][]
    _mtm?: Array<Record<string, unknown>>
  }
}

export function Matomo({ siteId, baseUrl }: MatomoProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (document.getElementById('matomo-script')) return

    window._paq = window._paq || []
    window._paq.push(['disableCookies'])
    window._paq.push(['setSiteId', siteId])
    window._paq.push(['setTrackerUrl', `${baseUrl}/matomo.php`])

    const g = document.createElement('script')
    g.async = true
    g.src = `${baseUrl}/matomo.js`
    g.id = 'matomo-script'
    document.head.appendChild(g)

    return () => {
      document.getElementById('matomo-script')?.remove()
      window._paq = []
    }
  }, [baseUrl, siteId])

  return null
}

export function MatomoTagManager({ containerId, baseUrl }: MatomoTagManagerProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (document.getElementById('matomo-tag-manager-script')) return

    if (!window._mtm) {
      window._mtm = []
    }
    window._mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' })

    const script = document.createElement('script')
    script.async = true
    script.src = `${baseUrl}/js/${containerId}.js`
    script.id = 'matomo-tag-manager-script'

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }

    return () => {
      document.getElementById('matomo-tag-manager-script')?.remove()
      window._mtm = []
    }
  }, [baseUrl, containerId])

  return null
}


type MatomoPageViewProps = {
  enabled: boolean
  trackInitialImmediately?: boolean
}

export function MatomoPageView({ enabled, trackInitialImmediately = true }: MatomoPageViewProps) {
  const router = useRouter()
  const initialTracked = useRef(false)
  const lastTrackedUrl = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    let timeout: number | undefined

    const pushPageView = (url: string) => {
      if (lastTrackedUrl.current === url) return

      window._paq = window._paq || []
      window._paq.push(['setCustomUrl', url])
      window._paq.push(['trackPageView'])
      lastTrackedUrl.current = url
    }

    if (trackInitialImmediately && !initialTracked.current) {
      initialTracked.current = true
      pushPageView(window.location.pathname + window.location.search)
    } else if (enabled) {
      pushPageView(window.location.pathname + window.location.search)
    }

    if (!enabled) return () => undefined

    const handleRouteChange = (url: string) => {
      if (timeout) window.clearTimeout(timeout)
      timeout = window.setTimeout(() => {
        pushPageView(url)
      }, 200)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
      if (timeout) window.clearTimeout(timeout)
    }
  }, [enabled, router.events, trackInitialImmediately])

  return null
}
